// =====================================================================
//  Validation d'un paquet à l'import (docs/TEMPLATE_PACKAGE_FORMAT.md § 5)
//  ---------------------------------------------------------------------
//  1. Lecture JSON (E01, E05)            2. Conformité au format (E02-E04)
//  3. Analyse Handlebars (E06, W01-W06)  4. Section OTP (E08, W10)
//  5. Contexte d'import (E09, W07, W08, W11)
//  6. Fusion d'essai par le vrai moteur via /process/preview (E07)
// =====================================================================

import {
    IEasymailPackage,
    OTP_RESERVED_VARIABLES,
    PACKAGE_FORMAT,
    PACKAGE_MAX_BYTES,
} from "../../Model/Package/IEasymailPackage.js";
import {PackageReport} from "./PackageReport.js";
import {HandlebarsAnalyzer} from "./HandlebarsAnalyzer.js";
import {PackageMapper} from "./PackageMapper.js";
import {ITemplateModel} from "../../Model/Template/ITemplateModel.js";

/** Où l'utilisateur importe le paquet ; conditionne les contrôles de cohérence. */
export interface IImportContext {
    /**
     * create-template : liste des templates d'un projet (crée template [+ stratégie]).
     * create-otp      : liste des stratégies OTP d'un projet (crée template + stratégie).
     * update-template : éditeur de template ouvert (remplace son contenu).
     * update-otp      : éditeur de stratégie OTP ouvert (remplace sa configuration).
     */
    mode: "create-template" | "create-otp" | "update-template" | "update-otp";
    /** Primarykey du template / de la stratégie ouverte (modes update). */
    currentId?: string;
    /** Langues des traductions déjà présentes dans le template ouvert (update-template). */
    existingLanguages?: Array<string>;
    /**
     * Projet de rattachement (projet ouvert en création, projet du template ou
     * de la stratégie en mise à jour) : exigé par la prévisualisation serveur.
     */
    fkProjet: string;
}

export interface IValidationResult {
    report: PackageReport;
    /** Paquet lu, si le JSON a pu être analysé (même avec des erreurs de format). */
    package?: IEasymailPackage;
}

const GUID_RE = /^[0-9a-fA-F]{8}-([0-9a-fA-F]{4}-){3}[0-9a-fA-F]{12}$/;
const TEMPLATE_KEYS = new Set(["name", "label", "typeContent", "typeProcess", "subject", "body", "sender"]);
const ROOT_KEYS = new Set(["format", "meta", "template", "modelTest", "translations", "otpStrategy", "notes"]);
const OTP_KEYS = new Set(["name", "label", "pattern", "expirationMinutes", "maxAttempts", "channels"]);

export class PackageValidator {

    public static Validate = async (raw: string, context: IImportContext): Promise<IValidationResult> => {
        const report = new PackageReport();

        const size = new TextEncoder().encode(raw).length;
        if (size > PACKAGE_MAX_BYTES)
            report.Error("E05", `contenu de ${size} octets (> 1 Mo)`);

        let parsed: unknown;
        try {
            parsed = JSON.parse(raw.replace(/^﻿/, ""));
        } catch (e) {
            report.Error("E01", PackageValidator.#jsonError(raw, e as Error));
            return {report};
        }
        if (!PackageValidator.#isObject(parsed)) {
            report.Error("E03", "la racine doit être un objet JSON");
            return {report};
        }

        const pkg = parsed as unknown as IEasymailPackage;
        PackageValidator.#checkStructure(pkg, report);
        const structureOk = !report.HasErrors;

        if (structureOk) {
            PackageValidator.#checkContent(pkg, report);
            PackageValidator.#checkOtp(pkg, report);
        }
        PackageValidator.#checkContext(pkg, context, report);

        // Fusion d'essai seulement si tout le reste est sain : inutile de
        // solliciter le serveur pour un paquet déjà refusé.
        if (!report.HasErrors)
            await PackageValidator.#checkMerge(pkg, context, report);

        return {report, package: pkg};
    };

    // --- 2. Conformité au format ---------------------------------------------

    static #checkStructure = (pkg: IEasymailPackage, r: PackageReport) => {
        if (pkg.format !== PACKAGE_FORMAT)
            r.Error("E02", `« format » doit valoir "${PACKAGE_FORMAT}" (trouvé : ${JSON.stringify(pkg.format)})`);

        Object.keys(pkg).filter(k => !ROOT_KEYS.has(k))
            .forEach(k => r.Warning("W09", `propriété racine inconnue « ${k} » (ignorée)`));

        const t = pkg.template as unknown;
        if (!PackageValidator.#isObject(t)) {
            r.Error("E03", "« template » manquant ou n'est pas un objet");
        } else {
            const tpl = pkg.template;
            PackageValidator.#needString(r, tpl.name, "template.name", 100);
            PackageValidator.#needString(r, tpl.subject, "template.subject");
            PackageValidator.#needString(r, tpl.body, "template.body");
            if (tpl.typeContent !== "html" && tpl.typeContent !== "text")
                r.Error("E03", `« template.typeContent » doit valoir "html" ou "text" (trouvé : ${JSON.stringify(tpl.typeContent)})`);
            if (tpl.typeProcess !== undefined && !["mail", "html", "pdf"].includes(tpl.typeProcess))
                r.Error("E03", `« template.typeProcess » doit valoir "mail", "html" ou "pdf" (trouvé : ${JSON.stringify(tpl.typeProcess)})`);
            if (typeof tpl.subject === "string" && /[\r\n]/.test(tpl.subject))
                r.Error("E03", "« template.subject » doit tenir sur une seule ligne");
            if (tpl.sender !== undefined && (!PackageValidator.#isObject(tpl.sender) ||
                !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(String(tpl.sender.email ?? ""))))
                r.Error("E03", "« template.sender.email » absent ou invalide");
            Object.keys(tpl).filter(k => !TEMPLATE_KEYS.has(k))
                .forEach(k => r.Warning("W09", `propriété inconnue « template.${k} »`));
        }

        if (typeof pkg.modelTest === "string")
            r.Error("E03", "« modelTest » doit être un objet JSON, pas une chaîne");
        else if (!PackageValidator.#isObject(pkg.modelTest))
            r.Error("E03", "« modelTest » manquant ou n'est pas un objet");

        if (pkg.translations !== undefined) {
            if (!Array.isArray(pkg.translations)) {
                r.Error("E03", "« translations » doit être un tableau");
            } else {
                const seen = new Set<string>();
                pkg.translations.forEach((tr, i) => {
                    const where = `translations[${i}]`;
                    if (!PackageValidator.#isObject(tr)) {
                        r.Error("E03", `« ${where} » doit être un objet`);
                        return;
                    }
                    if (typeof tr.language !== "string" || !/^[a-z]{2}$/.test(tr.language))
                        r.Error("E03", `« ${where}.language » doit être un code ISO 639-1 en minuscules`);
                    else if (tr.language === "fr")
                        r.Error("E04", `« ${where} » : "fr" est la langue du pivot, pas une traduction`);
                    else if (seen.has(tr.language))
                        r.Error("E04", `« ${where} » : langue "${tr.language}" en double`);
                    seen.add(tr.language);
                    PackageValidator.#needString(r, tr.subject, `${where}.subject`);
                    PackageValidator.#needString(r, tr.body, `${where}.body`);
                    if (typeof tr.subject === "string" && /[\r\n]/.test(tr.subject))
                        r.Error("E03", `« ${where}.subject » doit tenir sur une seule ligne`);
                });
            }
        }

        if (pkg.otpStrategy !== undefined)
            PackageValidator.#checkOtpStructure(pkg, r);

        const src = pkg.meta?.source;
        if (pkg.meta !== undefined && !PackageValidator.#isObject(pkg.meta))
            r.Error("E03", "« meta » doit être un objet");
        else if (src !== undefined && (!PackageValidator.#isObject(src) || !GUID_RE.test(String(src.templateId ?? ""))))
            r.Error("E03", "« meta.source.templateId » doit être un GUID");
        else if (src?.otpStrategyId !== undefined && !GUID_RE.test(String(src.otpStrategyId)))
            r.Error("E03", "« meta.source.otpStrategyId » doit être un GUID");

        if (pkg.notes !== undefined && typeof pkg.notes !== "string")
            r.Error("E03", "« notes » doit être une chaîne");
    };

    /** Bornes alignées sur OtpStrategyModelValidator (C#) et les CHECK SQL de OTP_STRATEGY. */
    static #checkOtpStructure = (pkg: IEasymailPackage, r: PackageReport) => {
        const s = pkg.otpStrategy;
        if (!PackageValidator.#isObject(s)) {
            r.Error("E03", "« otpStrategy » doit être un objet");
            return;
        }
        PackageValidator.#needString(r, s.name, "otpStrategy.name", 100);
        const p = s.pattern;
        if (!PackageValidator.#isObject(p)) {
            r.Error("E03", "« otpStrategy.pattern » manquant ou n'est pas un objet");
        } else {
            PackageValidator.#needInt(r, p.length, "otpStrategy.pattern.length", 4, 12);
            if (!["numeric", "alphanumeric", "alphanumericNoAmbiguous"].includes(p.alphabet as string))
                r.Error("E03", "« otpStrategy.pattern.alphabet » doit valoir \"numeric\", \"alphanumeric\" ou \"alphanumericNoAmbiguous\"");
            if (p.case !== undefined && !["upper", "lower", "mixed"].includes(p.case))
                r.Error("E03", "« otpStrategy.pattern.case » doit valoir \"upper\", \"lower\" ou \"mixed\"");
            if (p.groupSize !== undefined)
                PackageValidator.#needInt(r, p.groupSize, "otpStrategy.pattern.groupSize", 0, 6);
            if (p.groupSeparator !== undefined && (typeof p.groupSeparator !== "string" || p.groupSeparator.length > 5))
                r.Error("E03", "« otpStrategy.pattern.groupSeparator » : chaîne de 5 caractères maximum");
        }
        PackageValidator.#needInt(r, s.expirationMinutes, "otpStrategy.expirationMinutes", 1, 60);
        PackageValidator.#needInt(r, s.maxAttempts, "otpStrategy.maxAttempts", 1, 10);
        if (!Array.isArray(s.channels) || !s.channels.length ||
            s.channels.some(c => c !== "mail" && c !== "sms") || new Set(s.channels).size !== s.channels.length)
            r.Error("E03", "« otpStrategy.channels » : tableau non vide de \"mail\" / \"sms\", sans doublon");
        Object.keys(s).filter(k => !OTP_KEYS.has(k))
            .forEach(k => r.Warning("W09", `propriété inconnue « otpStrategy.${k} »`));
    };

    // --- 3. Analyse Handlebars -----------------------------------------------

    static #checkContent = (pkg: IEasymailPackage, r: PackageReport) => {
        const t = pkg.template;
        const typeProcess = t.typeProcess ?? "mail";
        if (/<[a-zA-Z][^>]*>|&nbsp;/.test(t.subject))
            r.Warning("W06", "le sujet contient du HTML ou des entités : il est envoyé en texte brut");

        const pivotRefs = new Set<string>([
            ...HandlebarsAnalyzer.Analyse(t.subject, pkg.modelTest, r, {label: "sujet", isBody: false}),
            ...HandlebarsAnalyzer.Analyse(t.body, pkg.modelTest, r, {
                label: "corps", isBody: true, typeContent: t.typeContent, typeProcess,
            }),
        ]);

        (pkg.translations ?? []).forEach((tr, i) => {
            const refs = new Set<string>([
                ...HandlebarsAnalyzer.Analyse(tr.subject, pkg.modelTest, r, {label: `sujet[${tr.language}]`, isBody: false}),
                ...HandlebarsAnalyzer.Analyse(tr.body, pkg.modelTest, r, {
                    label: `corps[${tr.language}]`, isBody: true, typeContent: t.typeContent, typeProcess,
                }),
            ]);
            const diff = [...refs].filter(x => !pivotRefs.has(x))
                .concat([...pivotRefs].filter(x => !refs.has(x)))
                .sort();
            if (diff.length)
                r.Warning("W05", `translations[${i}] (${tr.language}) n'utilise pas les mêmes variables que le pivot : ${diff.join(", ")}`);
        });
    };

    // --- 4. Section OTP --------------------------------------------------------

    static #checkOtp = (pkg: IEasymailPackage, r: PackageReport) => {
        const s = pkg.otpStrategy;
        if (!s)
            return;
        if ((pkg.template.typeProcess ?? "mail") !== "mail")
            r.Error("E08", "un template OTP doit avoir typeProcess = \"mail\"");
        if (!s.channels.includes("mail"))
            r.Error("E08", "« otpStrategy.channels » doit contenir \"mail\" (seul canal actif)");
        if (!/\{\{\s*Code\s*\}\}/.test(pkg.template.body))
            r.Error("E08", "le corps d'un template OTP doit afficher {{Code}} (casse exacte)");
        if (s.channels.includes("sms"))
            r.Warning("W10", "le canal SMS est configurable mais pas encore actif : seul le mail sera envoyé");
        if (!pkg.template.sender)
            r.Advice("A06", "template OTP sans « sender » : le runtime OTP utilise l'expéditeur du template, à renseigner (onglet Sujet) avant l'envoi");

        const keys = Object.keys(pkg.modelTest);
        OTP_RESERVED_VARIABLES.forEach(v => {
            if (!keys.includes(v))
                r.Warning("W10", `modelTest d'un template OTP : ajouter « ${v} » (injectée par EASYMAIL) pour la prévisualisation`);
        });
        keys.filter(k => !OTP_RESERVED_VARIABLES.includes(k) &&
            OTP_RESERVED_VARIABLES.some(v => v.toLowerCase() === k.toLowerCase()))
            .forEach(k => r.Warning("W10", `« ${k} » : homonyme d'une variable réservée, sera écartée du contexte à l'exécution`));
    };

    // --- 5. Contexte d'import ------------------------------------------------

    static #checkContext = (pkg: IEasymailPackage, c: IImportContext, r: PackageReport) => {
        const hasOtp = pkg.otpStrategy !== undefined;
        const src = PackageValidator.#isObject(pkg.meta) ? pkg.meta.source : undefined;

        switch (c.mode) {
            case "create-otp":
                if (!hasOtp)
                    r.Error("E09", "ce paquet ne contient pas de stratégie OTP (« otpStrategy ») : importez-le depuis l'onglet Templates");
                break;
            case "update-otp":
                if (!hasOtp)
                    r.Error("E09", "ce paquet ne contient pas de stratégie OTP (« otpStrategy ») à appliquer");
                r.Warning("W11", "seule la configuration OTP est importée ici ; le contenu du template (sujet, corps, modèle) se met à jour en important ce paquet dans le template lié");
                if (src?.otpStrategyId && c.currentId && src.otpStrategyId.toLowerCase() !== c.currentId.toLowerCase())
                    r.Warning("W07", "ce paquet a été exporté depuis une autre stratégie OTP");
                break;
            case "update-template":
                if (hasOtp)
                    r.Warning("W11", "la section « otpStrategy » est ignorée ici ; importez ce paquet dans la stratégie OTP pour mettre à jour sa configuration");
                if (src?.templateId && c.currentId && src.templateId.toLowerCase() !== c.currentId.toLowerCase())
                    r.Warning("W07", "ce paquet a été exporté depuis un autre template");
                if (Array.isArray(pkg.translations) || pkg.translations === undefined) {
                    const incoming = new Set((pkg.translations ?? []).map(t => t.language));
                    const stale = (c.existingLanguages ?? []).filter(l => !incoming.has(l));
                    if (stale.length)
                        r.Warning("W08", `traductions conservées mais probablement obsolètes : ${stale.map(l => l.toUpperCase()).join(", ")} — relancer « Traduire »`);
                }
                break;
        }
    };

    // --- 6. Fusion d'essai -----------------------------------------------------

    static #checkMerge = async (pkg: IEasymailPackage, c: IImportContext, r: PackageReport) => {
        const code = PackageMapper.ModelTestToCode(pkg);
        const versions = [{label: "fr", subject: pkg.template.subject, body: pkg.template.body}]
            .concat((pkg.translations ?? []).map(t => ({label: t.language, subject: t.subject, body: t.body})));

        for (const v of versions) {
            // Template non enregistré (Primarykey vide) : la prévisualisation
            // exige néanmoins un Nom et un projet (TemplateModelValidator).
            const model: ITemplateModel = {
                Nom: pkg.template.name,
                FkProjet: c.fkProjet,
                TypeContent: PackageMapper.TypeContentToInt(pkg.template.typeContent),
                TypeProcess: PackageMapper.TypeProcessToInt(pkg.template.typeProcess),
                SubjectTemplate: v.subject,
                BodyTemplate: v.body,
                Datas: [{Code: code}],
            };
            const failure = await PackageValidator.#tryPreview(model);
            if (failure !== null)
                r.Error("E07", `fusion d'essai en échec (${v.label.toUpperCase()}) : ${failure}`);
        }
    };

    /**
     * Appelle /process/preview directement (et non via FetchHelper) pour lire
     * le message d'erreur du moteur sans notification générique. Renvoie null
     * si la fusion réussit, sinon un message court exploitable par l'agent IA.
     */
    static #tryPreview = async (model: ITemplateModel): Promise<string | null> => {
        try {
            const response = await fetch("/process/preview", {
                method: "POST",
                headers: {"Content-Type": "application/json", "Accept": "text/plain"},
                body: JSON.stringify(model),
            });
            if (response.ok)
                return null;
            return PackageValidator.#engineMessage(await response.text(), response.status);
        } catch (e) {
            return `appel au moteur impossible (${(e as Error).message})`;
        }
    };

    /** Extrait la ligne utile d'une réponse d'erreur (texte, JSON de validation ou page d'exception). */
    static #engineMessage = (body: string, status: number): string => {
        try {
            const json = JSON.parse(body);
            const errors = json?.errors ?? json?.Errors;
            if (errors)
                return Object.values(errors).flat().map((x: any) => x?.Message ?? x).join(" ; ");
        } catch {
            // pas du JSON
        }
        const firstLine = body.replace(/<[^>]+>/g, " ").split(/\r?\n/).map(x => x.trim()).find(x => x.length > 0) ?? "";
        const exception = /([A-Za-z.]*Exception[^:]*:\s*[^\r\n]{0,300})/.exec(body)?.[1];
        return (exception ?? firstLine).slice(0, 300) || `erreur HTTP ${status}`;
    };

    // --- Utilitaires -----------------------------------------------------------

    static #needString = (r: PackageReport, v: unknown, where: string, maxLength?: number) => {
        if (typeof v !== "string" || !v.trim())
            r.Error("E03", `« ${where} » obligatoire (chaîne non vide)`);
        else if (maxLength && v.length > maxLength)
            r.Error("E03", `« ${where} » dépasse ${maxLength} caractères`);
    };

    static #needInt = (r: PackageReport, v: unknown, where: string, min: number, max: number) => {
        if (typeof v !== "number" || !Number.isInteger(v) || v < min || v > max)
            r.Error("E03", `« ${where} » doit être un entier entre ${min} et ${max}`);
    };

    static #isObject = (v: unknown): v is Record<string, any> =>
        v !== null && typeof v === "object" && !Array.isArray(v);

    /** Message JSON.parse enrichi de la ligne et de la colonne. */
    static #jsonError = (raw: string, e: Error): string => {
        const msg = e?.message ?? String(e);
        const lc = /line (\d+) column (\d+)/.exec(msg);
        if (lc)
            return `JSON invalide ligne ${lc[1]}, colonne ${lc[2]} : ${msg}`;
        const pos = /position (\d+)/.exec(msg);
        if (pos) {
            const before = raw.slice(0, Number(pos[1]));
            const line = (before.match(/\n/g) ?? []).length + 1;
            const col = before.length - before.lastIndexOf("\n");
            return `JSON invalide ligne ${line}, colonne ${col} : ${msg}`;
        }
        return `JSON invalide : ${msg}`;
    };
}
